import { lazy, type ReactNode, Suspense, startTransition, useState } from "react";
import { AccountButton, SwitcherButton, ThemeButton } from "./rail-buttons";

/**
 * The shell's overlays load on first use, so a page downloads none of them before someone
 * reaches for one (scripts/budget.ts counts everything else against the route budget):
 *
 * - the rail's menus (switcher, theme, account) and the phone menu: `menus.tsx`, with Radix's
 *   menus, popper and dialog; fetched when a pointer or focus reaches a rail control;
 * - the ⌘K palette (cmdk): fetched when a modifier key goes down or Search is reached.
 *
 * Until its menu has loaded, a rail control is the same button (rail-buttons.tsx). Pressing it
 * loads the menu in a transition, so the button stays in place, then the menu opens at once.
 */

const loadMenus = () => import("./menus");

/** Starts fetching the menus chunk. */
export const prefetchMenus = () => void loadMenus();

const Switcher = lazy(() => loadMenus().then((m) => ({ default: m.Switcher })));
const ThemeToggle = lazy(() => loadMenus().then((m) => ({ default: m.ThemeToggle })));
const AccountMenu = lazy(() => loadMenus().then((m) => ({ default: m.AccountMenu })));
export const MobileMenu = lazy(() => loadMenus().then((m) => ({ default: m.MobileMenu })));

const loadPalette = () => import("./command-palette");

/** Starts fetching the palette chunk. */
export const prefetchPalette = () => void loadPalette();

export const CommandPalette = lazy(() =>
  loadPalette().then((m) => ({ default: m.CommandPalette })),
);

/**
 * A rail control: its button until pressed, then its menu, opened. `menu` renders the loaded
 * component; it stays mounted afterwards and opens and closes as any menu does.
 */
function OnDemand({
  button,
  menu,
}: {
  button: (props: { onClick: () => void }) => ReactNode;
  menu: ReactNode;
}) {
  const [active, setActive] = useState(false);
  const placeholder = button({ onClick: () => startTransition(() => setActive(true)) });
  return <Suspense fallback={placeholder}>{active ? menu : placeholder}</Suspense>;
}

const intent = { onPointerEnter: prefetchMenus, onFocus: prefetchMenus };

export function SwitcherControl({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <OnDemand
      button={({ onClick }) => (
        <SwitcherButton aria-expanded={false} onClick={onClick} {...intent} />
      )}
      menu={<Switcher onNavigate={onNavigate} defaultOpen />}
    />
  );
}

export function ThemeControl() {
  return (
    <OnDemand
      button={({ onClick }) => <ThemeButton aria-expanded={false} onClick={onClick} {...intent} />}
      menu={<ThemeToggle defaultOpen />}
    />
  );
}

export function AccountControl() {
  return (
    <OnDemand
      button={({ onClick }) => (
        <AccountButton aria-expanded={false} onClick={onClick} {...intent} />
      )}
      menu={<AccountMenu defaultOpen />}
    />
  );
}
